import { Box, Button, Typography, Paper, useTheme, Stack } from "@mui/material";
import { ErrorOutline, Refresh, Home } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { ModulePath } from "../../constant/ApplicationVariables";

export const ErrorFallbackPage = ({
  error,
  resetErrorBoundary,
  title = "Something went wrong",
  message = "We're sorry for the inconvenience. Please try refreshing the page or return to the projects.",
  fullScreen = true,
  compact = false,
  showRetryButton = true,
  showHomeButton = true,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: fullScreen ? "100vh" : compact ? 200 : 360,
        width: "100%",
        backgroundColor: fullScreen
          ? theme.palette.background.default
          : "transparent",
        p: compact ? 2 : 3,
      }}
    >
      <Paper
        elevation={compact ? 0 : 2}
        sx={{
          maxWidth: 520,
          width: "100%",
          p: compact ? 3 : 4,
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* Accent bar */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.warning.main})`,
          }}
        />

        <Stack spacing={compact ? 2 : 3} alignItems="center" sx={{ mt: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: compact ? 56 : 72,
              height: compact ? 56 : 72,
              borderRadius: "50%",
              backgroundColor: isDark
                ? "rgba(244,67,54,0.15)"
                : "rgba(244,67,54,0.08)",
            }}
          >
            <ErrorOutline
              sx={{
                fontSize: compact ? 32 : 40,
                color: theme.palette.error.main,
              }}
            />
          </Box>

          <Typography
            variant={compact ? "h6" : "h5"}
            component="h1"
            textAlign="center"
            fontWeight={600}
            color="text.primary"
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            textAlign="center"
            color="text.secondary"
            sx={{ lineHeight: 1.6, px: compact ? 0 : 1 }}
          >
            {message}
          </Typography>

          {error?.message && (
            <Box
              sx={{
                width: "100%",
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.04)"
                  : "rgba(0,0,0,0.03)",
                border: `1px dashed ${theme.palette.divider}`,
                maxHeight: 120,
                overflow: "auto",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  color: "text.secondary",
                  wordBreak: "break-word",
                  display: "block",
                }}
              >
                {error.message}
              </Typography>
            </Box>
          )}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ mt: 1, width: { xs: "100%", sm: "auto" } }}
          >
            {showRetryButton && resetErrorBoundary && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Refresh />}
                onClick={resetErrorBoundary}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: 2,
                  minWidth: 140,
                }}
              >
                Try Again
              </Button>
            )}

            {showHomeButton && (
              <Button
                variant="outlined"
                startIcon={<Home />}
                onClick={() => navigate(ModulePath.projects)}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: 2,
                  minWidth: 140,
                  borderColor: theme.palette.divider,
                  color: "text.secondary",
                  "&:hover": {
                    borderColor: theme.palette.text.secondary,
                    backgroundColor: "action.hover",
                  },
                }}
              >
                Go to Projects
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*                              AppErrorBoundary                              */
/* -------------------------------------------------------------------------- */

export const AppErrorBoundary = ({ children, ...fallbackProps }) => (
  <ErrorBoundary
    FallbackComponent={(props) => (
      <ErrorFallbackPage {...props} {...fallbackProps} />
    )}
    onError={(error, info) => {
      console.error("Error:", error, info.componentStack);
      localStorage.setItem(
        "UI-Error",
        JSON.stringify({
          error: error?.toString() || "Unknown Error",
          info,
          time: new Date(),
        })
      );
      // Add Sentry / logging service here
    }}
    onReset={() => window.location.reload()}
  >
    {children}
  </ErrorBoundary>
);